import { createTheme } from '@mui/material/styles';
import { alpha } from '@mui/material';

// Extend the theme to include custom color properties
declare module '@mui/material/styles' {
  interface Palette {
    header: Palette['primary'];
  }
  interface PaletteOptions {
    header?: PaletteOptions['primary'];
  }
}

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#7C3AED', // Medium purple for active elements and buttons
    },
    secondary: {
      main: '#5B21B6', // Deep purple for branding accents
    },
    background: {
      default: '#111827', // Dark main background
      paper: '#1F2937',   // Cards and elevated surfaces
    },
    text: {
      primary: '#F9FAFB', // Main text color
      secondary: '#9CA3AF', // Lighter text for secondary info
    },
    success: {
      main: '#10B981',
    },
    error: {
      main: '#EF4444',
    },
    info: {
      main: '#3B82F6',
    },
    warning: {
      main: '#F59E0B',
    },
    divider: '#374151', // For borders and dividers
    header: {
      main: '#1F2937', // Using the 'paper' color for the header
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 'bold',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#1F2937', // Use a neutral, dark background for the table header
          borderBottom: '1px solid #374151',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: '#F9FAFB', // Use primary text color for header cells
          fontWeight: 'bold',
        },
        body: {
          borderColor: '#374151', // Match divider color for cell borders
        }
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1F2937', // Match header color
          backgroundImage: 'none', // Remove any default gradients
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Ensure no gradients on paper components
        }
      }
    }
  },
});

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#5B21B6', // Deep purple for better contrast on light backgrounds
    },
    secondary: {
      main: '#7C3AED', // Lighter purple for secondary elements
    },
    background: {
      default: '#F8FAFC', // Clean, off-white background
      paper: '#FFFFFF',   // Pure white for cards and surfaces
    },
    text: {
      primary: '#111827', // Dark grey for main text
      secondary: '#374151', // Lighter grey for secondary text
    },
    success: {
      main: '#10B981',
    },
    error: {
      main: '#EF4444',
    },
    info: {
      main: '#3B82F6',
    },
    warning: {
      main: '#F59E0B',
    },
    divider: '#E5E7EB', // Soft grey for dividers
    header: {
      main: '#FFFFFF',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 'bold',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#5B21B6', 0.08),
          borderBottom: '1px solid #E5E7EB',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: '#111827',
          fontWeight: 'bold',
        },
        body: {
          borderColor: '#E5E7EB',
        }
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          backgroundImage: 'none',
          boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.06), 0px 4px 5px 0px rgba(0,0,0,0.04), 0px 1px 10px 0px rgba(0,0,0,0.04)',
          borderBottom: '1px solid #E5E7EB',
          color: '#111827'
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        }
      }
    }
  },
});
